// Copyright 2018 Sorama B.V.

import { GenericMeasurement } from "./SoramaWebApi";

export interface OrderMode {
    name: string;
    icon: string;
    orderMethod: (
        a: GenericMeasurement,
        b: GenericMeasurement,
        selector?: (x: GenericMeasurement) => GenericMeasurement
    ) => number;
}

export class OrderModes {
    public static readonly NameAscending: OrderMode = {
        name: "Alphabetical Ascending",
        icon: "fas fa-sort-alpha-down",
        orderMethod: (
            a: GenericMeasurement,
            b: GenericMeasurement,
            selector?: (x: GenericMeasurement) => GenericMeasurement
        ) => {
            a = selector ? selector(a) : a;
            b = selector ? selector(b) : b;

            const aName = a.name ? <string>a.name : "";
            const bName = b.name ? <string>b.name : "";

            if (aName === bName) return 0;

            return aName.localeCompare(bName);
        },
    };
    public static readonly NameDescending: OrderMode = {
        name: "Alphabetical Descending",
        icon: "fas fa-sort-alpha-up",
        orderMethod: (
            a: GenericMeasurement,
            b: GenericMeasurement,
            selector?: (x: GenericMeasurement) => GenericMeasurement
        ) => {
            a = selector ? selector(a) : a;
            b = selector ? selector(b) : b;

            const aName = a.name ? <string>a.name : "";
            const bName = b.name ? <string>b.name : "";

            if (aName === bName) return 0;

            return -aName.localeCompare(bName);
        },
    };

    public static readonly MeasuredAscending: OrderMode = {
        name: "Measured Ascending",
        icon: "fas fa-sort-numeric-down",
        orderMethod: (
            a: GenericMeasurement,
            b: GenericMeasurement,
            selector?: (x: GenericMeasurement) => GenericMeasurement
        ) => {
            a = selector ? selector(a) : a;
            b = selector ? selector(b) : b;

            const aCreated = a.start ? new Date(a.start).getTime() : 0;
            const bCreated = b.start ? new Date(b.start).getTime() : 0;

            if (aCreated === bCreated) return 0;

            return aCreated > bCreated ? 1 : -1;
        },
    };

    public static readonly MeasuredDescending: OrderMode = {
        name: "Measured Descending",
        icon: "fas fa-sort-numeric-up",
        orderMethod: (
            a: GenericMeasurement,
            b: GenericMeasurement,
            selector?: (x: GenericMeasurement) => GenericMeasurement
        ) => {
            a = selector ? selector(a) : a;
            b = selector ? selector(b) : b;

            const aCreated = a.start ? new Date(a.start).getTime() : 0;
            const bCreated = b.start ? new Date(b.start).getTime() : 0;

            if (aCreated === bCreated) return 0;

            return aCreated < bCreated ? 1 : -1;
        },
    };

    public static readonly DurationAscending: OrderMode = {
        name: "Duration Ascending",
        icon: "fas fa-sort-amount-up",
        orderMethod: (
            a: GenericMeasurement,
            b: GenericMeasurement,
            selector?: (x: GenericMeasurement) => GenericMeasurement
        ) => {
            a = selector ? selector(a) : a;
            b = selector ? selector(b) : b;

            const aDuration = a.duration ? <number>a.duration : 0;
            const bDuration = b.duration ? <number>b.duration : 0;

            if (aDuration === bDuration) return 0;

            return aDuration > bDuration ? 1 : -1;
        },
    };

    public static readonly DurationDescending: OrderMode = {
        name: "Duration Descending",
        icon: "fas fa-sort-amount-down",
        orderMethod: (
            a: GenericMeasurement,
            b: GenericMeasurement,
            selector?: (x: GenericMeasurement) => GenericMeasurement
        ) => {
            a = selector ? selector(a) : a;
            b = selector ? selector(b) : b;

            const aDuration = a.duration ? <number>a.duration : 0;
            const bDuration = b.duration ? <number>b.duration : 0;

            if (aDuration === bDuration) return 0;

            return aDuration < bDuration ? 1 : -1;
        },
    };

    public static readonly All: OrderMode[] = [
        OrderModes.NameAscending,
        OrderModes.NameDescending,
        OrderModes.MeasuredAscending,
        OrderModes.MeasuredDescending,
        OrderModes.DurationAscending,
        OrderModes.DurationDescending,
    ];
}
